import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Never let a proxy/browser serve a stale response on GET — most
  // pages set their own `?_ts=...` cache-bust, but a hard no-store
  // header ensures every axios call is fresh.
  headers: { "Cache-Control": "no-store" },
});

// Attach bearer token from localStorage as fallback (Safari 3rd-party cookie issues).
// EXCEPT on auth endpoints: sending a stale token there confuses backend flows.
api.interceptors.request.use((config) => {
  const url = config.url || "";
  const isAuthEntry =
    url.includes("/auth/login") ||
    url.includes("/auth/recovery-reset") ||
    url.includes("/auth/refresh");
  if (isAuthEntry) {
    delete config.headers.Authorization;
    return config;
  }
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// -------- Silent refresh on 401 --------
// If any /api call returns 401, attempt to swap the refresh token for a fresh
// access token, then retry the original request. Only after a refresh itself
// fails do we clear state and bounce to /giris.
let refreshPromise = null;

async function silentRefresh() {
  if (!refreshPromise) {
    const storedRefresh = localStorage.getItem("refresh_token");
    refreshPromise = axios
      .post(
        `${API}/auth/refresh`,
        storedRefresh ? { refresh_token: storedRefresh } : {},
        { withCredentials: true },
      )
      .then((res) => {
        const { access_token, refresh_token } = res.data || {};
        if (access_token) localStorage.setItem("access_token", access_token);
        if (refresh_token) localStorage.setItem("refresh_token", refresh_token);
        return access_token;
      })
      .catch((e) => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        throw e;
      })
      .finally(() => {
        // Small delay before allowing next refresh so bursts of 401s coalesce
        setTimeout(() => { refreshPromise = null; }, 200);
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error.response?.status;
    const original = error.config || {};
    const url = original.url || "";
    const isAuthEntry =
      url.includes("/auth/login") ||
      url.includes("/auth/recovery-reset") ||
      url.includes("/auth/refresh");

    if (status === 401 && !isAuthEntry && !original._retried) {
      try {
        const newToken = await silentRefresh();
        original._retried = true;
        if (newToken) original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/giris")) {
          window.location.assign("/giris");
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
